import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, ButtonGroup, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Row, Col } from "reactstrap";
import { moneda } from "./pagosCxpPDF";

// (2026-10) Factura del taller sobre una nota: el IVA se suma a toda la nota o
// sólo a la parte que facturó. nota = { _id, folio, subtotal, impuestos, iva,
// total, abonado, factura, facturaParcial, importeFacturado }.
const r2 = (n) => Math.round(Number(n || 0) * 100) / 100;

function ModalFactura({ isOpen, toggle, nota, onSaved }) {
  const [folio, setFolio] = useState("");
  const [modo, setModo] = useState("total"); // sin | total | parcial
  const [tasa, setTasa] = useState(16);
  const [importe, setImporte] = useState(0);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!isOpen || !nota) return;
    const t = Number(nota.impuestos || 0);
    setFolio(nota.factura && nota.factura !== "NA" ? nota.factura : "");
    setTasa(t > 0 ? t : 16);
    setModo(t > 0 ? (nota.facturaParcial === "Si" ? "parcial" : "total") : "total");
    setImporte(nota.facturaParcial === "Si" ? nota.importeFacturado : nota.subtotal);
  }, [isOpen, nota]);

  if (!nota) return null;
  const subtotal = Number(nota.subtotal || 0);
  const base = modo === "sin" ? 0 : modo === "parcial" ? Math.min(Number(importe || 0), subtotal) : subtotal;
  const iva = r2((base * Number(tasa || 0)) / 100);
  const nuevoTotal = r2(subtotal + iva);
  const muyBajo = nuevoTotal < Number(nota.abonado || 0) - 0.01;
  const parcialMal = modo === "parcial" && !(Number(importe) > 0 && Number(importe) <= subtotal + 0.01);
  const tasaMal = modo !== "sin" && !(Number(tasa) > 0 && Number(tasa) <= 100);

  const guardar = async () => {
    setGuardando(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_URL_CXP_PAGOS}Factura/${nota._id}`,
        {
          factura: folio.trim(),
          impuestos: modo === "sin" ? 0 : Number(tasa),
          parcial: modo === "parcial",
          importeFacturado: modo === "parcial" ? Number(importe) : 0,
        },
        { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
      );
      Swal.fire("Listo", "Factura guardada; la nota ya trae el nuevo total", "success");
      toggle();
      if (onSaved) onSaved();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "No se pudo guardar la factura", "error");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Factura · Nota {nota.folio}</ModalHeader>
      <ModalBody>
        <Label>Folio de la factura</Label>
        <Input value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="Ej. A-1520" autoFocus />
        <br />
        <Label>IVA</Label>
        <br />
        <ButtonGroup>
          {[
            ["sin", "Sin IVA"],
            ["total", "Toda la nota"],
            ["parcial", "Sólo una parte"],
          ].map(([v, l]) => (
            <Button key={v} size="sm" color={modo === v ? "info" : "secondary"} onClick={() => setModo(v)}>
              {l}
            </Button>
          ))}
        </ButtonGroup>
        {modo !== "sin" ? (
          <Row style={{ marginTop: 12 }}>
            <Col md={modo === "parcial" ? 6 : 12}>
              <Label>% de IVA</Label>
              <Input type="number" min="0" max="100" step="any" value={tasa} onChange={(e) => setTasa(e.target.value)} />
            </Col>
            {modo === "parcial" ? (
              <Col md={6}>
                <Label>Importe facturado (sin IVA)</Label>
                <Input type="number" min="0" step="any" value={importe} onChange={(e) => setImporte(e.target.value)} />
              </Col>
            ) : null}
          </Row>
        ) : null}
        <br />
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            <tr>
              <td>Subtotal de la nota</td>
              <td align="right">{moneda(subtotal)}</td>
            </tr>
            {modo === "parcial" ? (
              <tr>
                <td>Facturado</td>
                <td align="right">{moneda(base)}</td>
              </tr>
            ) : null}
            <tr>
              <td>IVA</td>
              <td align="right">{moneda(iva)}</td>
            </tr>
            <tr>
              <td>
                <b>Total</b>
              </td>
              <td align="right">
                {moneda(nota.total)} → <b>{moneda(nuevoTotal)}</b>
              </td>
            </tr>
          </tbody>
        </table>
        {parcialMal ? (
          <p className="text-danger" style={{ marginTop: 8 }}>
            Lo facturado debe ser mayor a 0 y no pasar del subtotal ({moneda(subtotal)}).
          </p>
        ) : null}
        {muyBajo ? (
          <p className="text-danger" style={{ marginTop: 8 }}>
            A la nota ya se le abonaron {moneda(nota.abonado)}; el total no puede quedar abajo de eso.
          </p>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={guardar} disabled={guardando || muyBajo || parcialMal || tasaMal}>
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ModalFactura;
